import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import type { Serie, Question } from '../types';

export default function AdminSeries() {
  const [series, setSeries] = useState<Serie[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [serieQuestions, setSerieQuestions] = useState<Record<number, Question[]>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; description: string; intruQuestionId: string; intruJustification: string; intruPoints: string }>({
    name: '', description: '', intruQuestionId: '', intruJustification: '', intruPoints: '200',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [s, q] = await Promise.all([
        api.get<Serie[]>('/api/series'),
        api.get<Question[]>('/api/questions'),
      ]);
      setSeries(s);
      setQuestions(q);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const createSerie = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      setCreating(true);
      const s = await api.post<Serie>('/api/series', { name: newName.trim(), description: newDesc.trim() || null });
      setSeries((prev) => [...prev, s]);
      setNewName('');
      setNewDesc('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteSerie = async (id: number) => {
    if (!window.confirm('Supprimer cette série ?')) return;
    try {
      await api.delete(`/api/series/${id}`);
      setSeries((prev) => prev.filter((s) => s.id !== id));
      setQuestions((prev) => prev.map((q) => q.serieId === id ? { ...q, serieId: null } : q));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const startEdit = (s: Serie) => {
    setEditingId(s.id);
    setEditForm({
      name: s.name,
      description: s.description || '',
      intruQuestionId: s.intruQuestionId ? String(s.intruQuestionId) : '',
      intruJustification: s.intruJustification || '',
      intruPoints: String(s.intruPoints || 200),
    });
  };

  const saveSerie = async (id: number) => {
    try {
      const updated = await api.put<Serie>(`/api/series/${id}`, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        intruQuestionId: editForm.intruQuestionId ? parseInt(editForm.intruQuestionId) : null,
        intruJustification: editForm.intruJustification.trim() || null,
        intruPoints: parseInt(editForm.intruPoints) || 200,
      });
      setSeries((prev) => prev.map((s) => s.id === id ? updated : s));
      setEditingId(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!serieQuestions[id]) {
      try {
        const qs = await api.get<Question[]>(`/api/series/${id}/questions`);
        setSerieQuestions((prev) => ({ ...prev, [id]: qs }));
      } catch { /* ignore */ }
    }
  };

  const assignQuestion = async (questionId: number, serieId: number | null) => {
    try {
      await api.putForm(`/api/questions/${questionId}`, (() => {
        const fd = new FormData();
        fd.append('serieId', serieId ? String(serieId) : '');
        return fd;
      })());
      setQuestions((prev) => prev.map((q) => q.id === questionId ? { ...q, serieId } : q));
      if (serieId && expandedId === serieId) {
        const qs = await api.get<Question[]>(`/api/series/${serieId}/questions`);
        setSerieQuestions((prev) => ({ ...prev, [serieId]: qs }));
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="page-loading">Chargement...</div>;

  const unassigned = questions.filter((q) => !q.serieId);

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Séries de questions</h2>
        <p className="text-muted">Organisez vos questions en séries. Configurez l'intru par série.</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Create serie */}
      <div className="card">
        <h3 className="card-title">Nouvelle série</h3>
        <form onSubmit={createSerie} className="serie-form">
          <input
            placeholder="Nom de la série *"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <input
            placeholder="Description (optionnel)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={creating || !newName.trim()}>
            {creating ? '...' : '+ Créer'}
          </button>
        </form>
      </div>

      {/* Series list */}
      {series.map((s) => {
        const sQs = serieQuestions[s.id] || questions.filter((q) => q.serieId === s.id);
        const intruQuestion = s.intruQuestionId ? questions.find((q) => q.id === s.intruQuestionId) : null;

        return (
          <div key={s.id} className="card serie-card">
            <div className="serie-header">
              {editingId === s.id ? (
                <div className="serie-edit-form">
                  <div className="form-row">
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Nom *"
                      className="serie-name-input"
                    />
                    <input
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Description"
                      className="serie-desc-input"
                    />
                  </div>

                  <div className="intru-config">
                    <h5 className="intru-config-title">Configuration de l'intru</h5>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Question intru</label>
                        <select
                          value={editForm.intruQuestionId}
                          onChange={(e) => setEditForm((f) => ({ ...f, intruQuestionId: e.target.value }))}
                        >
                          <option value="">— Aucun intru —</option>
                          {sQs.map((q) => (
                            <option key={q.id} value={q.id}>
                              {q.songTitle || q.title}{q.artist ? ` — ${q.artist}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Points intru</label>
                        <input
                          type="number"
                          min="0"
                          value={editForm.intruPoints}
                          onChange={(e) => setEditForm((f) => ({ ...f, intruPoints: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Justification admin (révélée aux joueurs)</label>
                      <textarea
                        value={editForm.intruJustification}
                        onChange={(e) => setEditForm((f) => ({ ...f, intruJustification: e.target.value }))}
                        placeholder="Ex: Cette chanson est en anglais, les autres sont en français."
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="btn-row">
                    <button className="btn btn-sm btn-success" onClick={() => saveSerie(s.id)}>✓ Sauvegarder</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Annuler</button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="serie-name">{s.name}</h4>
                    {s.description && <p className="serie-desc">{s.description}</p>}
                    <div className="serie-badges">
                      <span className="badge">
                        {questions.filter((q) => q.serieId === s.id).length} questions
                      </span>
                      {intruQuestion ? (
                        <span className="badge badge-intru">
                          🕵 Intru: {intruQuestion.songTitle || intruQuestion.title}{intruQuestion.artist ? ` — ${intruQuestion.artist}` : ''} ({s.intruPoints} pts)
                        </span>
                      ) : (
                        <span className="badge badge-warning">⚠ Aucun intru défini</span>
                      )}
                    </div>
                  </div>
                  <div className="serie-actions">
                    <button className="btn btn-sm btn-secondary" onClick={() => startEdit(s)}>✏ Modifier</button>
                    <button className="btn btn-sm" onClick={() => toggleExpand(s.id)}>
                      {expandedId === s.id ? '▲ Masquer' : '▼ Questions'}
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteSerie(s.id)}>🗑</button>
                  </div>
                </>
              )}
            </div>

            {expandedId === s.id && editingId !== s.id && (
              <div className="serie-questions">
                <h5>Questions dans cette série ({sQs.length})</h5>
                {sQs.length === 0 ? (
                  <p className="text-muted">Aucune question</p>
                ) : (
                  sQs.map((q, qi) => (
                    <div key={q.id} className="serie-question-item">
                      <span className="serie-question-num">#{qi + 1}</span>
                      <div className="serie-question-info">
                        <span className="serie-question-title">{q.songTitle || q.title}</span>
                        {q.artist && <span className="serie-question-artist">— {q.artist}</span>}
                      </div>
                      <div className="serie-question-badges">
                        <span className="badge">{q.answerType === 'multiple_choice' ? 'QCM' : q.answerType === 'buzzer' ? 'Buzzer' : 'Texte'}</span>
                        <span className="badge">{q.maxPoints} pts</span>
                        {q.durationMs > 0 && <span className="badge">{Math.round(q.durationMs / 1000)}s</span>}
                        {q.dynamicScoring && <span className="badge badge-accent">⏱</span>}
                        {s.intruQuestionId === q.id && <span className="badge badge-intru">🕵 intru</span>}
                      </div>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => assignQuestion(q.id, null)}
                      >Retirer</button>
                    </div>
                  ))
                )}

                {unassigned.length > 0 && (
                  <div className="add-to-serie">
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) assignQuestion(parseInt(e.target.value), s.id);
                      }}
                    >
                      <option value="">+ Ajouter une question...</option>
                      {unassigned.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.songTitle || q.title}{q.artist ? ` — ${q.artist}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {series.length === 0 && (
        <div className="empty-state">
          <p>Aucune série. Créez-en une ci-dessus.</p>
        </div>
      )}
    </div>
  );
}
